import { useCallback, useEffect, useMemo } from "react"
import { ApiPrintMetaShopOrderPost, MetaPrintShopOrderGetResponse } from "../../../../api/sift/front/api-print-meta-shop-order";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useParams } from "react-router-dom";
import { PathParams, RoutingPath } from "../../../../routes/routing-path";
import { printActions } from "../../../../slices/sift/print-slice";
import { dialogActions } from "../../../dialog/slice/dialog-slice";
import { apiActions } from "../../../../slices/api-slice";
import { DateFormatter } from "../../../../utilities/date-formatter";
import { replace } from "connected-react-router";
import { LaboOrderCompChecker } from "../../../../utilities/labo-order-comp-checker";

export const PRINT_META_STATUS = {
  create: '注文作成中',
  created: '注文作成済',
  enableOrder: 'ラボ発注可',
  ordering: 'ラボ発注中',
} as const;

type Props = {
  isLockedAfterOrder?: boolean,
}

export const usePrintStatus = (props?: Props) => {
  const {
    isLockedAfterOrder,
  } = props ?? {};
  const dispatch = useAppDispatch();
  const {
    kijshopCd,
    shopOrderId,
    orderId,
  } = useParams<PathParams>();
  const {
    orderedList,
    currentMeta,
  } = useAppSelector((state) => ({
    orderedList: state.common.orderedList?.order || [],
    currentMeta: state.print.printShopOrderMeta[shopOrderId],
  }));

  const statusName = useMemo(() => {
    if (!currentMeta) return '';
    const target = orderedList.find((v) => v.shopOrderId === currentMeta.shopOrderId);
    if (target) return target.orderStatusName;
    if (
      currentMeta.orderCompleteDate
      && currentMeta.status === 'ラボ発注中'
      && LaboOrderCompChecker.checkTimeoutForPrint({ orderCompleteDate: currentMeta.orderCompleteDate ?? '' })
    ) {
      return '送信エラー';
    }
    return currentMeta.status;
  }, [
    orderedList,
    currentMeta,
  ]);
  const centerStatusName = useMemo(() => {
    if (!currentMeta) return '';
    const target = orderedList.find((v) => v.shopOrderId === currentMeta.shopOrderId);
    if (target) return target.orderStatusName;
    return null;
  }, [
    orderedList,
    currentMeta,
  ]);

  const isOrdered = useMemo(() => {
    const orderedStatus = ['ラボ発注中'];
    return orderedStatus.includes(statusName) || (statusName === centerStatusName);
  }, [
    statusName,
    centerStatusName,
  ]);


  const changeStatus = useCallback((
    status: keyof typeof PRINT_META_STATUS,
    callback?: {
      onDidError?: () => void,
      onError?: () => void,
    },
  ) => {
    return new Promise<void>((resolve) => {
      const statusValue = PRINT_META_STATUS[status];
      dispatch(printActions.getOrderMeta(
        kijshopCd, shopOrderId,
        {
          onSuccess: (res) => {
            dispatch(apiActions.run(new ApiPrintMetaShopOrderPost(
              {
                ...res,
                status: statusValue,
                lastUpdateDate: DateFormatter.date2str(new Date(), 'YYYYMMDD_HHmmSS', '/'),
              },
            ),
              {
                onSuccess: () => {
                  resolve();
                },
                onError: () => {
                  dispatch(dialogActions.pushMessage({
                    title: 'エラー',
                    message: [
                      '注文情報の更新に失敗しました。',
                    ],
                    buttons: [
                      { label: 'OK', callback: () => {
                        dispatch(dialogActions.pop());
                        callback?.onError?.();
                      }},
                    ],
                  }));

                },
              },
              { ignoreSystemError: true },
            ));
          },
          onError: () => {
            callback?.onDidError?.();
            dispatch(dialogActions.popAll());
            dispatch(dialogActions.pushMessage({
              title: 'エラー',
              message: [
                '注文情報の取得に失敗しました。',
              ],
              buttons: [
                { label: 'OK', callback: () => {
                  dispatch(dialogActions.pop());
                  callback?.onError?.();
                }},
              ],
            }));
          }
        }
      ));
    });
  }, [
    dispatch,
    kijshopCd, shopOrderId,
  ]);

  const changeLastUpdateDate = useCallback((callback?:
    {
      onDidError?: () => void,
      onError?: () => void,
    },
  ) => {
    return new Promise<void>((resolve) => {
      dispatch(printActions.getOrderMeta(
        kijshopCd, shopOrderId,
        {
          onSuccess: (res) => {
            dispatch(apiActions.run(new ApiPrintMetaShopOrderPost(
              {
                ...res,
                lastUpdateDate: DateFormatter.date2str(new Date(), 'YYYYMMDD_HHmmSS', '/'),
              },
            ),
              {
                onSuccess: () => {
                  resolve();
                },
                onError: () => {
                  dispatch(dialogActions.pushMessage({
                    title: 'エラー',
                    message: [
                      '注文情報の更新に失敗しました。',
                    ],
                    buttons: [
                      { label: 'OK', callback: () => {
                        dispatch(dialogActions.pop());
                        callback?.onError?.();
                      }},
                    ],
                  }));

                },
              },
              { ignoreSystemError: true },
            ));
          },
          onError: () => {
            callback?.onDidError?.();
            dispatch(dialogActions.popAll());
            dispatch(dialogActions.pushMessage({
              title: 'エラー',
              message: [
                '注文情報の取得に失敗しました。',
              ],
              buttons: [
                { label: 'OK', callback: () => {
                  dispatch(dialogActions.pop());
                  callback?.onError?.();
                }},
              ],
            }));
          }
        }
      ));
    })
  }, []);

  useEffect(() => {
    if (!isLockedAfterOrder) return;
    if (centerStatusName || (statusName === 'ラボ発注中')) {

      dispatch(replace(RoutingPath.cnv.printOrder({ kijshopCd, shopOrderId })))
    }
  }, [
    shopOrderId,
    kijshopCd,
    centerStatusName,
    statusName,
  ]);

  return {
    changeStatus,
    changeLastUpdateDate,
    statusName,
    centerStatusName,
    isOrdered,
  }
}
